import { ContentAudience } from "../../../types/contentAudience";
import type { AddPostPreset } from "../../add-post/preset";
import { UserListing } from "../../../types/userListing";
import { ContentLength, SocialPlatforms } from "../../campaign/types";
import { MediaItem } from "../../listings/listing/types";
import { ListingList } from "../../post/types";

export interface Time {
    hour: number;
    minute: number;
}

export interface BasicInfoStepProps {
    openHouseDate?: Date;
    setOpenHouseDate?: (date: Date) => void;
    startTime?: Time; // { hour: number; minute: number }
    setStartTime?: (value: Time) => void; // Function to update start time
    endTime?: Time; // { hour: number; minute: number }
    setEndTime?: (value: Time) => void; // Function to update end time
    startPeriod?: string; // "AM" or "PM"
    setStartPeriod?: (period: string) => void; // Function to update start period (AM/PM)
    endPeriod?: string; // "AM" or "PM"
    setEndPeriod?: (period: string) => void; // Function to update end period (AM/PM)
    generalInstructions: string; // General instructions for the open house
    setGeneralInstructions: (value: string) => void; // Function to update general instructions
    aiEnhance: boolean; // Flag to indicate if AI enhancement is enabled
    setAiEnhance: (value: boolean) => void; // Function to update AI enhancement flag
    videoPostType?: VideoPostType | null;
    setVideoPostType?: (value: VideoPostType | null) => void;
    schoolDistrict?: string;
    setSchoolDistrict?: (value: string) => void;
    tags?: string[];
    setTags?: (value: string[]) => void;
    listing?: UserListing;
    isVideoPost: boolean;
    isOpenHousePost: boolean;
    isAdHocPost: boolean;
    listings?: ListingList[];
    setListings?: (value: ListingList[]) => void;
    selectedListing?: ListingList | undefined;
    setSelectedListing?: (listing: ListingList | undefined) => void;
    confirmAiEnhanceDisable: () => boolean;
    onTokensValidChange?: (allFilled: boolean) => void; // Callback for token validation
    // Lead Generation props for state persistence
    leadGenerationType?: string;
    setLeadGenerationType?: (value: string) => void;
    leadGenerationSubtypes?: string[];
    setLeadGenerationSubtypes?: (value: string[]) => void;
    leadGenerationPrompts?: string[];
    setLeadGenerationPrompts?: (value: string[]) => void;
    selectedPrompt?: string;
    setSelectedPrompt?: (value: string) => void;
    promptParts?: Array<{ type: 'text' | 'token'; value: string; key?: string }>;
    setPromptParts?: React.Dispatch<React.SetStateAction<Array<{ type: 'text' | 'token'; value: string; key?: string }>>>;
    setAreTokensFilled?: React.Dispatch<React.SetStateAction<boolean>>; // Setter for token validation state
    hideContent?: boolean; // When true, hides the content textarea (manual flow moves content entry to its own step)
    hideAiEnhance?: boolean; // Listing Post card: the AI Enhance choice sits on the content step after Platforms
    postCaptionedVideo?: boolean;
    setPostCaptionedVideo?: (value: boolean) => void;
    // MB-1461: "Review captions before rendering" — opt-in pre-render
    // transcript review for the captioned-video flow.
    reviewCaptions?: boolean;
    setReviewCaptions?: (value: boolean) => void;
    // Voice family for this one piece. Only surfaced for Lead
    // Generation, MyBrand and My Niche Video; every other flow leaves it
    // alone and the api falls back to Residential.
    contentAudience?: ContentAudience;
    setContentAudience?: (value: ContentAudience) => void;
    // My Niche Video: AI Pro with the Niche Finder allowance.
    nicheEligible?: boolean;
    // The Add Post page's card: hides the captioned checkbox, filters or hides the
    // campaign dropdown and picks the row for the user (see add-post/preset.ts).
    preset?: AddPostPreset;
    // The niche overview has answered (eligible or not, or failed). Until then the My Niche
    // Video card says nothing about availability: the rows may land before the overview.
    nicheResolved?: boolean;
}

export interface ContentCreationStepProps {
    generalInstructions: string;
    setGeneralInstructions: (value: string) => void;
    sharedContent: string;
    platformContent: Record<string, string>;
    selectedPlatforms: string[];
    handlePlatformContentChange: (platformId: string, content: string) => void;
    getPlatformById: (platformId: string) => Platform | undefined;
    activeTab: string;
    setActiveTab: (tab: string) => void;
}

export interface PlatformSelectionStepProps {
    availablePlatforms: Platform[];
    selectedPlatforms: string[];
    handlePlatformToggle: (platformId: string) => void;
}

export interface MediaSelectionStepProps {
    useSharedMedia: boolean;
    toggleUseSharedMedia: () => void;
    selectedPlatforms: string[];
    media: MediaCollection;
    sharedMedia: string[];
    openMediaPicker: (platformId?: string) => void;
    getPlatformById: (id: string) => Platform | undefined;
    platformMedia: Record<string, string[]>;
    getMediaCounts: (platformId: string) => {
        imageCount: number;
        videoCount: number;
    };
}

export interface ContentReviewStepProps {
    listingAddress: string; // Address of the listing
    openHouseDate?: Date; // Date of the open house
    startTime?: Time; // Start time of the open house (hour and minute)
    endTime?: Time; // End time of the open house (hour and minute)
    startPeriod?: string; // Start period (AM/PM)
    endPeriod?: string; // End period (AM/PM)
    selectedPlatforms: Platform[]; // Array of selected platform IDs
    platformContent: PlatformContentMap; // Content for each platform (platformId => content)
    setPlatformContent: React.Dispatch<
        React.SetStateAction<PlatformContentMap>
    >;
    platformSchedules: Record<string, InitialSchedule>;
    setPlatformSchedules: React.Dispatch<
        React.SetStateAction<Record<string, InitialSchedule>>
    >;
    selectedMedia: string[]; // Selected media IDs
    setSelectedMedia: (mediaIds: string[]) => void; // Setter for selected media
    availableMedia?: MediaCollection; // Available media for the post
    setShouldSyncParentState?: (open: boolean) => void;
    shouldSyncParentState?: boolean;
    platformGraphics?: PlatformGraphicsMap; // Graphics settings for each platform
    setPlatformGraphics?: React.Dispatch<React.SetStateAction<PlatformGraphicsMap>>; // Setter for platform graphics
    selectedListing?: ListingList | undefined;
    hideValidation?: boolean; // Hide validation messages (used in step 4)
    hideDatetime?: boolean; // Hide date/time display in post previews (used in step 4)
    setHasScheduleChangesInStep4?: React.Dispatch<React.SetStateAction<boolean>>; // Track if schedule changes were made in step 4
    youtubeProfileUrl?: string;
    platformProfiles?: Record<string, string>;
    initialMediaType?: string;
    // Persists an explicit Images / Video segment click made in the Edit Media
    // dialog back into the wizard, so it survives a reopen (MB-1492).
    onMediaTypeChange?: (type: string) => void;
    isVideo?: boolean; // True when post is a video-only showcase (e.g. YouTube); false for mixed/image posts
    isYouTubeShort?: boolean | null;
    fetchMedia?: () => Promise<void>;
    hideFooter?: boolean; // When true, hides Post Footer in edit dialog (manual flow)
    hideEditMedia?: boolean; // When true, hides the Edit Media button (text only flow)
    instagramHashtagViolated?: boolean; // True when Instagram content exceeds the 5-hashtag limit
}

export interface Platform {
    id: SocialPlatforms;
    name: string;
    characterLimit: number;
    requiresMedia: boolean;
    maxImages: number;
    maxVideos: number;
}

export interface MediaCollection {
    images?: MediaItem[];
    videos?: MediaItem[];
    assets?: MediaItem[];
}

export interface PlatformContent {
    content: string;
    title?: string;
    imageText?: string; // Separate text for the graphic/image
    hashtags?: string[]; // YouTube hashtags stored separately from content
}

export interface PlatformGraphics {
    bgColor: {
        type: 'solid' | 'gradient';
        color?: string;
        from?: string;
        to?: string;
        direction?: string;
    };
    textColor: string;
    fontSize: number;
    fontFamily: string;
}

export interface PlatformContentMap {
    facebook: PlatformContent;
    instagram: PlatformContent;
    twitter: PlatformContent;
    linkedin: PlatformContent;
    pinterest: PlatformContent;
    google: PlatformContent;
    youtube: PlatformContent;
}

export interface PlatformGraphicsMap {
    facebook: PlatformGraphics;
    instagram: PlatformGraphics;
    twitter: PlatformGraphics;
    linkedin: PlatformGraphics;
    pinterest: PlatformGraphics;
    google: PlatformGraphics;
}

export enum VideoPostType {
    Walkthrough = "Walkthrough",
    SchoolDistrict = "SchoolDistrict",
    Feature = "Feature",
    Neighborhood = "Neighborhood",
}

export function makeInitialSchedule(platform?: string, footer?: string) {
    const now = new Date();
    const safeDate = new Date(now.getTime() + 30 * 60_000);

    let h24 = safeDate.getHours();
    let m = safeDate.getMinutes();
    const rem = m % 15;
    if (rem !== 0) {
        m += 15 - rem;
        if (m === 60) {
            m = 0;
            h24 = (h24 + 1) % 24;
        }
    }

    const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
    const period = h24 >= 12 ? "PM" : "AM";

    return {
        date: safeDate,
        hour: h12,
        minute: m,
        period,
        contentLength:
            platform === "instagram"
                ? ContentLength.Short
                : ContentLength.Medium,
        footer: platform === "instagram" ? "" : footer || "",
        includeFooter: true,
        footerType: (platform === "pinterest" || platform === "google" ? "in-post" : "comment") as "comment" | "in-post",
        threads: true,
        emojis: true,
        usePrice: true,
    };
}

export type InitialSchedule = {
    date: Date;
    hour: number;
    minute: number;
    period: string;
    contentLength: ContentLength;
    footer: string;
    includeFooter: boolean;
    footerType: "comment" | "in-post";
    threads: boolean;
    emojis?: boolean;
    usePrice?: boolean;
    youtubeVisibility?: "public" | "private";
    notifySubscribers?: boolean;
};

export interface PlatformSettingsProps {
    platformSchedules: Record<string, InitialSchedule>;
    setPlatformSchedules: React.Dispatch<
        React.SetStateAction<Record<string, InitialSchedule>>
    >;
    setPlatformContent: React.Dispatch<
        React.SetStateAction<PlatformContentMap>
    >;
}
